import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { AlertCircle, CheckCircle, Loader2, RefreshCw, Send, WifiOff, XCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import { lumi } from '../lib/lumi'
import ResearcherSurveyModal, { ResearcherSurvey } from '../components/ResearcherSurveyModal'
import {
  cachePublicSurvey,
  getCachedPublicSurvey,
  getOfflineQueueCount,
  syncOfflineSubmissions,
} from '../lib/offlineSurveyQueue'

const PublicSurveyResponder: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>()
  const [survey, setSurvey] = useState<ResearcherSurvey | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [cancelledProgress, setCancelledProgress] = useState<boolean | null>(null)
  const [offlineCount, setOfflineCount] = useState(0)
  const [isSyncing, setIsSyncing] = useState(false)
  const [sessionKey, setSessionKey] = useState(0)

  const updateQueueCount = (surveyId?: string) => {
    setOfflineCount(getOfflineQueueCount(surveyId ? { surveyId } : undefined))
  }

  useEffect(() => {
    const handleQueueChange = () => {
      if (survey?._id) updateQueueCount(survey._id)
    }
    const handleOnline = async () => {
      if (survey?._id) {
        updateQueueCount(survey._id)
        const pending = getOfflineQueueCount({ surveyId: survey._id })
        if (pending > 0) {
          try {
            const res = await syncOfflineSubmissions({ surveyId: survey._id })
            if (res.sent > 0) {
              toast.success(`${res.sent} resposta(s) sincronizada(s) com sucesso!`)
            }
          } catch { /* ignore */ }
          updateQueueCount(survey._id)
        }
      }
    }

    window.addEventListener('dna_offline_queue_changed', handleQueueChange)
    window.addEventListener('online', handleOnline)
    return () => {
      window.removeEventListener('dna_offline_queue_changed', handleQueueChange)
      window.removeEventListener('online', handleOnline)
    }
  }, [survey?._id])

  useEffect(() => {
    if (!token.trim()) {
      setError('Link de pesquisa inválido.')
      setLoading(false)
      return
    }

    const loadSurvey = async () => {
      setLoading(true)
      setError('')

      if (!navigator.onLine) {
        const cached = getCachedPublicSurvey(token)
        if (cached) {
          setSurvey(cached)
          updateQueueCount(cached._id)
          setLoading(false)
          return
        }
      }

      try {
        const response = await lumi.entities.surveys.list({
          filter: {
            publicResponseToken: token.trim(),
            publicResponseEnabled: true,
          },
          limit: 1,
        }) as { list?: ResearcherSurvey[] }

        const foundSurvey = response.list?.[0]
        if (!foundSurvey) {
          const cached = getCachedPublicSurvey(token)
          if (cached) {
            setSurvey(cached)
            updateQueueCount(cached._id)
            setLoading(false)
            return
          }
          setError('Pesquisa não encontrada ou link público desativado.')
          return
        }

        if (foundSurvey.status !== 'active') {
          setError('Esta pesquisa não está aceitando respostas no momento.')
          return
        }

        setSurvey(foundSurvey)
        cachePublicSurvey(token, foundSurvey)
        updateQueueCount(foundSurvey._id)
      } catch (err) {
        console.error('Erro ao carregar pesquisa pública:', err)
        const cached = getCachedPublicSurvey(token)
        if (cached) {
          setSurvey(cached)
          updateQueueCount(cached._id)
        } else {
          setError('Não foi possível carregar a pesquisa. Verifique sua conexão e tente novamente.')
        }
      } finally {
        setLoading(false)
      }
    }

    void loadSurvey()
  }, [token])

  const handleManualSync = async () => {
    if (!navigator.onLine) {
      toast.error('Dispositivo offline. Conecte-se à internet para sincronizar.')
      return
    }
    if (!survey?._id) return

    setIsSyncing(true)
    try {
      const res = await syncOfflineSubmissions({ surveyId: survey._id })
      if (res.sent > 0) {
        toast.success(`${res.sent} resposta(s) enviada(s) com sucesso!`)
      } else {
        toast('Nenhuma resposta pendente para sincronizar.', { icon: 'ℹ️' })
      }
    } catch {
      toast.error('Erro ao sincronizar respostas offline.')
    } finally {
      setIsSyncing(false)
      updateQueueCount(survey._id)
    }
  }

  const handleRestartSurvey = () => {
    const safeSurveyId = survey?._id || (survey as any)?.id || ''
    if (safeSurveyId) {
      try {
        window.localStorage.removeItem(`public_survey_draft:${safeSurveyId}`)
      } catch { /* ignore */ }
    }
    if (survey?._id && survey._id !== safeSurveyId) {
      try {
        window.localStorage.removeItem(`public_survey_draft:${survey._id}`)
      } catch { /* ignore */ }
    }
    toast.dismiss()
    setSubmitted(false)
    setCancelledProgress(null)
    setSessionKey((prev) => prev + 1)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
        <Loader2 size={36} className="animate-spin text-blue-600 mb-3" />
        <p className="text-sm font-medium text-slate-600">Carregando questionário...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 text-center shadow-sm space-y-4">
          <div className="h-12 w-12 rounded-full bg-red-100 text-red-600 flex items-center justify-center mx-auto">
            <AlertCircle size={26} />
          </div>
          <h2 className="text-lg font-bold text-slate-900">Acesso Indisponível</h2>
          <p className="text-sm text-slate-600">{error}</p>
        </div>
      </div>
    )
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 text-center shadow-sm space-y-5">
          <div className="h-16 w-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto shadow-inner">
            <CheckCircle size={36} />
          </div>
          <div className="space-y-1">
            <h2 className="text-xl font-bold text-slate-900">Obrigado por responder!</h2>
            <p className="text-sm text-slate-600">
              Sua resposta foi registrada com sucesso no sistema.
            </p>
          </div>

          {offlineCount > 0 && (
            <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 text-left space-y-2">
              <div className="flex items-center gap-2 text-amber-800 font-bold text-xs uppercase tracking-wider">
                <WifiOff size={14} />
                <span>Respostas salvas neste aparelho</span>
              </div>
              <p className="text-xs text-amber-700">
                Existem {offlineCount} resposta(s) acumulada(s) localmente aguardando conexão com a internet.
              </p>
              <button
                onClick={handleManualSync}
                disabled={isSyncing}
                className="w-full mt-2 inline-flex items-center justify-center gap-2 py-2 px-3 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs transition-colors disabled:opacity-50"
              >
                {isSyncing ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                <span>Sincronizar Agora</span>
              </button>
            </div>
          )}

          <div className="pt-3 border-t border-slate-100 space-y-2">
            <button
              onClick={handleRestartSurvey}
              className="w-full inline-flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm transition-colors shadow-sm"
            >
              <RefreshCw size={15} />
              <span>Responder Novamente / Próximo Entrevistado</span>
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (cancelledProgress) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 text-center shadow-sm space-y-5">
          <div className="h-16 w-16 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mx-auto">
            <XCircle size={36} />
          </div>
          <div className="space-y-1">
            <h2 className="text-xl font-bold text-slate-900">Entrevista Cancelada</h2>
            <p className="text-sm text-slate-600">
              O preenchimento foi encerrado antes da conclusão.
            </p>
          </div>

          <div className="pt-3 border-t border-slate-100">
            <button
              onClick={handleRestartSurvey}
              className="w-full inline-flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm transition-colors shadow-sm"
            >
              <RefreshCw size={15} />
              <span>Iniciar Nova Entrevista</span>
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!survey) return null

  return (
    <ResearcherSurveyModal
      key={`${survey._id || 'survey'}_${sessionKey}`}
      survey={survey}
      researcherId="public_link"
      researcherName="Link Público"
      isPublicLink
      onClose={() => setCancelledProgress(true)}
      onCancelled={(savedProgress) => {
        if (savedProgress) {
          setCancelledProgress(true)
        } else {
          handleRestartSurvey()
        }
      }}
      onSubmitted={() => {
        setSubmitted(true)
        if (survey._id) updateQueueCount(survey._id)
      }}
    />
  )
}

export default PublicSurveyResponder
